export interface Photo {
  PhotoKey: string;
  WorkOrderDetailKey: string;
  BlobPath: string;
  OriginalBlobPath: string | null;
  FileName: string;
  FileSize: number;
  MimeType: string;
  Comment: string | null;
  Stage: string;
  Position: string;
  DateTaken: string | null;
  UploadStatus: string;
  ProcessingStatus: string | null;
  AlphaAngle: string | null;
  imageUrl: string;
  thumbnailUrl: string;
  originalImageUrl: string;
}

export interface UploadUrlResponse {
  photoKey: string;
  originalUploadUrl: string;
  croppedUploadUrl: string;
}

export type PhotoViewMode = 'medium' | 'large' | 'list';

export interface PhotoSlot {
  stage: string;
  position: string;
  label: string;
}

export const PHOTO_SLOTS: PhotoSlot[] = [
  { stage: 'pre-test', position: 'top', label: 'Pre-test — Top' },
  { stage: 'pre-test', position: 'bottom', label: 'Pre-test — Bottom' },
  { stage: '1', position: 'top', label: 'Stage 1 — Top' },
  { stage: '1', position: 'bottom', label: 'Stage 1 — Bottom' },
  { stage: '2', position: 'top', label: 'Stage 2 — Top' },
  { stage: '2', position: 'bottom', label: 'Stage 2 — Bottom' },
  { stage: '3', position: 'top', label: 'Stage 3 — Top' },
  { stage: '3', position: 'bottom', label: 'Stage 3 — Bottom' },
  { stage: '4', position: 'top', label: 'Stage 4 — Top' },
  { stage: '4', position: 'bottom', label: 'Stage 4 — Bottom' },
  { stage: '5', position: 'top', label: 'Stage 5 — Top' },
  { stage: '5', position: 'bottom', label: 'Stage 5 — Bottom' },
];

export type AlphaAngle = 'low' | 'average' | 'high';

// REQ-2026-0009: warning + hard-reject metadata mirrored from backend.
export interface PhotoWarning {
  code: 'large-file' | 'exif-rotation-applied' | 'gps-stripped';
  message: string;
}
export interface PhotoHardReject {
  code: 'unsupported-format' | 'file-too-large' | 'decode-failed' | 'invalid-dimensions';
  message: string;
}

export interface BatchUploadResult {
  sortOrder: number;
  stage: string;
  position: string;
  photoKey: string;
  processingStatus: string;
  error?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  // REQ-2026-0009
  warnings?: PhotoWarning[];
  rejected?: PhotoHardReject;
}

export interface BatchProgressEvent {
  type: 'uploading' | 'processing' | 'saving' | 'done' | 'error' | 'complete';
  index: number;
  total: number;
  fileName: string;
  stage?: string;
  position?: string;
  status?: string;
  error?: string;
  results?: BatchUploadResult[];
}

/**
 * BUG-2026-0137 §11 + rework — tall portrait crop (W:H = 3:5) for
 * full-core-with-ruler photos: the Rock TCS/UCS/UCS-NS report photo cells
 * render at this ratio (kit token `photo.tallCoreAspect`; the lab asked for
 * exactly 3:5 to match their classic reports). Their photo tabs pass it as
 * the CropDialog default AND swap the preset list for the rock one below.
 */
export const PHOTO_TALL_CORE_ASPECT = 3 / 5;

export interface AspectPreset {
  label: string;
  value: number | undefined;
}

export const ASPECT_PRESETS: readonly AspectPreset[] = [
  // Report ratio is 1:1 since 2026-08-05 (square crops fill the DS report's
  // square photo cells in every dot-convention orientation; was 7:8).
  { label: 'Report (1:1)', value: 1 },
  { label: 'Free', value: undefined },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
];

/** Rock TCS / UCS / UCS-NS crop presets: ONE report ratio — 3:5 — plus the
 *  generic ones (BUG-2026-0137/0138 rework: 7:8 and 7:10 removed). */
export const ROCK_REPORT_ASPECT_PRESETS: readonly AspectPreset[] = [
  { label: 'Report (3:5)', value: PHOTO_TALL_CORE_ASPECT },
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
];

/** Rock BTS report photo ratio (W:H) — 8:7 LANDSCAPE, matching the BTS
 *  report's photo cells (BUG-2026-0172 rework, Shawn Hegger 2026-08-12:
 *  "wider than it is tall"; was 7:8 portrait from 2026-08-05). */
export const BTS_REPORT_ASPECT = 8 / 7;

/** Rock BTS disc crop presets: the report ratio first (crops made with it
 *  fill the BTS report cells edge to edge), then the generic ones. */
export const BTS_DISC_ASPECT_PRESETS: readonly AspectPreset[] = [
  { label: 'Report (8:7)', value: BTS_REPORT_ASPECT },
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
];
